import { cn } from '@/utils/cn';
import {
  LayoutGrid,
  Briefcase,
  Package,
  Truck,
  Users,
  Wrench,
  MoreHorizontal,
  Plus,
  Building2
} from 'lucide-react';
import type { CategoryType } from '../types/categoriesList.types';

interface CategoryTabsProps {
  activeTab: CategoryType | 'all';
  onChange: (value: CategoryType | 'all') => void;
  onAdd?: () => void;
}

const tabs: Array<{ value: CategoryType | 'all'; label: string; icon: any; tone: string }> = [
  { value: 'all', label: 'Tất cả', icon: LayoutGrid, tone: 'emerald' },
  { value: 'work', label: 'Công việc', icon: Briefcase, tone: 'blue' },
  { value: 'material', label: 'Vật tư', icon: Package, tone: 'teal' },
  { value: 'equipment', label: 'Thiết bị', icon: Truck, tone: 'amber' },
  { value: 'personnel', label: 'Nhân sự', icon: Users, tone: 'indigo' },
  { value: 'department', label: 'Phòng ban', icon: Building2, tone: 'violet' },
  { value: 'service', label: 'Dịch vụ', icon: Wrench, tone: 'rose' },
  { value: 'other', label: 'Khác', icon: MoreHorizontal, tone: 'slate' },
];

export function CategoryTabs({ activeTab, onChange, onAdd }: CategoryTabsProps) {
  const getStyles = (tone: string, isActive: boolean) => {
    const colors: Record<string, { active: string, inactive: string, iconActive: string, iconInactive: string, border: string }> = {
      emerald: { active: 'bg-emerald-700 text-white border-emerald-700', inactive: 'bg-emerald-50 text-emerald-700 border-emerald-100', iconActive: 'bg-white/20', iconInactive: 'bg-emerald-100 text-emerald-600', border: 'border-emerald-100' },
      blue: { active: 'bg-blue-700 text-white border-blue-700', inactive: 'bg-blue-50 text-blue-700 border-blue-100', iconActive: 'bg-white/20', iconInactive: 'bg-blue-100 text-blue-600', border: 'border-blue-100' },
      teal: { active: 'bg-teal-700 text-white border-teal-700', inactive: 'bg-teal-50 text-teal-700 border-teal-100', iconActive: 'bg-white/20', iconInactive: 'bg-teal-100 text-teal-600', border: 'border-teal-100' },
      amber: { active: 'bg-amber-700 text-white border-amber-700', inactive: 'bg-amber-50 text-amber-700 border-amber-100', iconActive: 'bg-white/20', iconInactive: 'bg-amber-100 text-amber-600', border: 'border-amber-100' },
      indigo: { active: 'bg-indigo-700 text-white border-indigo-700', inactive: 'bg-indigo-50 text-indigo-700 border-indigo-100', iconActive: 'bg-white/20', iconInactive: 'bg-indigo-100 text-indigo-600', border: 'border-indigo-100' },
      rose: { active: 'bg-rose-700 text-white border-rose-700', inactive: 'bg-rose-50 text-rose-700 border-rose-100', iconActive: 'bg-white/20', iconInactive: 'bg-rose-100 text-rose-600', border: 'border-rose-100' },
      violet: { active: 'bg-violet-700 text-white border-violet-700', inactive: 'bg-violet-50 text-violet-700 border-violet-100', iconActive: 'bg-white/20', iconInactive: 'bg-violet-100 text-violet-600', border: 'border-violet-100' },
      slate: { active: 'bg-slate-700 text-white border-slate-700', inactive: 'bg-slate-50 text-slate-700 border-slate-100', iconActive: 'bg-white/20', iconInactive: 'bg-slate-100 text-slate-600', border: 'border-slate-100' },
    };
    const style = colors[tone] || colors.slate;
    return {
      btn: isActive ? style.active : cn(style.inactive, style.border),
      icon: isActive ? style.iconActive : style.iconInactive
    };
  };

  return (
    <>
      {/* Mobile Menu: Ultra-Compact 4-Column Grid */}
      <div className="grid grid-cols-4 gap-1.5 lg:hidden">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.value;
          const Icon = tab.icon;
          const style = getStyles(tab.tone, isActive);

          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onChange(tab.value)}
              className={cn(
                'flex flex-col items-center justify-center gap-1 rounded-md px-1 py-2.5 transition-all duration-200 active:scale-95 border shadow-sm',
                style.btn
              )}
            >
              <div className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                style.icon
              )}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <span className="truncate text-xs leading-none">{tab.label}</span>
            </button>
          );
        })}
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="flex flex-col items-center justify-center gap-1 rounded-md bg-emerald-50 px-1 py-2.5 text-emerald-700 border border-emerald-100 active:scale-95 shadow-sm"
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-100">
              <Plus className="h-3.5 w-3.5" />
            </div>
            <span className="text-xs leading-none">Thêm</span>
          </button>
        )}
      </div>

      {/* Desktop Menu: Original Horizontal Scroll with Colors */}
      <div className="hidden lg:flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.value;
          const style = getStyles(tab.tone, isActive);

          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onChange(tab.value)}
              className={cn(
                'shrink-0 rounded-md px-4 py-2 text-sm transition-all duration-200 border shadow-sm',
                style.btn
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
